/**
 * Diagramme des Dashboards: leuchtende Donuts, Fortschrittsringe, isometrische
 * Halbringe und Pfeile. Alle Farben kommen als CSS-Werte herein (`var(--primary)`,
 * Statusfarben, Kritikalitätsfarben) – so folgen die Diagramme dem Farbprofil
 * und dem Hell-/Dunkelmodus. Das Leuchten steckt in `dashboard.css`.
 */

import { useId } from 'react';
import { arrowPolys, type Poly, proj, ringPolys, sectorPath, shade, smoothLine } from './iso';

const Polys = ({ items }: { items: Poly[] }) => (
  <>
    {items.map((p, i) => (
      <polygon key={i} points={p.points} style={{ fill: p.fill, stroke: p.stroke }} strokeWidth={p.stroke ? 0.6 : 0} />
    ))}
  </>
);

/** Kennzeichen mit Wert, wie die Wertmarken der Infografik. */
function Tag({ x, y, text, color }: { x: number; y: number; text: string; color: string }) {
  const w = Math.max(28, text.length * 7 + 14);
  return (
    <g className="dash-glow" style={{ ['--glow' as string]: color }}>
      <rect x={x - w / 2} y={y - 12} width={w} height={19} rx={9.5} style={{ fill: shade(color, -0.4) }} />
      <text x={x} y={y + 2} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff">
        {text}
      </text>
    </g>
  );
}

export type ColoredValue = { label: string; value: number; color: string };

/** Leuchtender Donut mit Prozentwerten; das erste Segment kann herausgezogen werden. */
export function GlowDonut({
  segments,
  centerValue,
  centerLabel,
  explodeFirst,
}: {
  segments: ColoredValue[];
  centerValue: string;
  centerLabel: string;
  explodeFirst?: boolean;
}) {
  const uid = useId().replace(/:/g, '');
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  let a = -Math.PI / 2;
  const parts = segments.map((s, i) => {
    const span = total ? (s.value / total) * Math.PI * 2 : 0;
    const mid = a + span / 2;
    const e = explodeFirst && i === 0 ? 9 : 0;
    const part = { s, i, a0: a + 0.03, a1: a + span - 0.03, mid, ox: e * Math.cos(mid), oy: e * Math.sin(mid), span };
    a += span;
    return part;
  });
  return (
    <svg viewBox="0 0 200 200" role="img" aria-label={`${centerLabel}: ${centerValue}. ${segments.map((s) => `${s.label} ${s.value}`).join(', ')}`} className="dash-donut">
      <defs>
        {parts.map((p) => (
          <linearGradient key={p.i} id={`${uid}-${p.i}`} gradientTransform={`rotate(${Math.round((p.mid * 180) / Math.PI + 90)} .5 .5)`}>
            <stop offset="0" style={{ stopColor: p.s.color }} />
            <stop offset="1" style={{ stopColor: shade(p.s.color, -0.4) }} />
          </linearGradient>
        ))}
      </defs>
      <circle cx="100" cy="100" r="72" fill="none" style={{ stroke: 'var(--surface-3)' }} strokeWidth="32" opacity="0.5" />
      {total > 0 &&
        parts.map((p) =>
          p.span > 0.05 ? (
            <g key={p.i} className="dash-glow" style={{ ['--glow' as string]: p.s.color }}>
              <path d={sectorPath(100 + p.ox, 100 + p.oy, 88, 56, p.a0, p.a1)} fill={`url(#${uid}-${p.i})`} />
              {p.span > 0.35 && (
                <text
                  x={100 + p.ox + 72 * Math.cos(p.mid)}
                  y={100 + p.oy + 72 * Math.sin(p.mid) + 4}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="700"
                  fill="#fff"
                >
                  {Math.round((p.s.value / total) * 100)}%
                </text>
              )}
            </g>
          ) : null,
        )}
      <text x="100" y="104" textAnchor="middle" fontSize="30" fontWeight="800" fill="var(--text)">
        {centerValue}
      </text>
      <text x="100" y="121" textAnchor="middle" fontSize="10.5" fill="var(--text-subtle)">
        {centerLabel}
      </text>
    </svg>
  );
}

/** Legende zu einem Donut oder Ring: Punkt, Name, Wert. */
export function DashLegend({ items }: { items: { label: string; value: string; color: string }[] }) {
  return (
    <div className="dash-legend">
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`}>
          <i className="dash-glow" style={{ background: it.color, ['--glow' as string]: it.color }} />
          <span className="truncate" title={it.label}>{it.label}</span>
          <b>{it.value}</b>
        </div>
      ))}
    </div>
  );
}

/** Konzentrische Fortschrittsringe – ein Ring je Eintrag, voll = Höchstwert. */
export function ProgressRings({
  items,
  centerValue,
  ariaLabel,
}: {
  items: { percent: number; color: string }[];
  centerValue: string;
  ariaLabel: string;
}) {
  const uid = useId().replace(/:/g, '');
  const r0 = 30;
  const step = Math.min(12.5, 62 / Math.max(1, items.length));
  const width = step * 0.6;
  return (
    <svg viewBox="0 0 200 200" role="img" aria-label={ariaLabel} className="dash-donut">
      <defs>
        {items.map((it, i) => (
          <linearGradient key={i} id={`${uid}-${i}`}>
            <stop offset="0" style={{ stopColor: it.color }} />
            <stop offset="1" style={{ stopColor: shade(it.color, -0.35) }} />
          </linearGradient>
        ))}
      </defs>
      {items.map((it, i) => {
        const r = r0 + i * step;
        const angle = -Math.PI / 2 + (Math.min(100, it.percent) / 100) * Math.PI * 2 * 0.999;
        return (
          <g key={i} className="dash-glow" style={{ ['--glow' as string]: it.color }}>
            <circle cx="100" cy="100" r={r} fill="none" style={{ stroke: 'var(--surface-3)' }} strokeWidth={width} opacity="0.6" />
            <path
              d={`M100,${100 - r} A${r},${r} 0 ${it.percent > 50 ? 1 : 0} 1 ${(100 + r * Math.cos(angle)).toFixed(1)},${(100 + r * Math.sin(angle)).toFixed(1)}`}
              fill="none"
              stroke={`url(#${uid}-${i})`}
              strokeWidth={width}
              strokeLinecap="round"
            />
          </g>
        );
      })}
      <text x="100" y="106" textAnchor="middle" fontSize="22" fontWeight="800" fill="var(--text)">
        {centerValue}
      </text>
    </svg>
  );
}

/** Zwei Verläufe als weiche Leuchtlinien über gleich langen Reihen. */
export function TrendLines({ series, ariaLabel }: { series: { values: number[]; color: string }[]; ariaLabel: string }) {
  const uid = useId().replace(/:/g, '');
  const W = 700;
  const H = 200;
  const max = Math.max(4, ...series.flatMap((s) => s.values)) * 1.15;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={ariaLabel} className="dash-trend">
      <defs>
        {series.map((s, i) => (
          <linearGradient key={i} id={`${uid}-${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: s.color }} stopOpacity="0.4" />
            <stop offset="1" style={{ stopColor: s.color }} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {[1, 2, 3, 4].map((i) => (
        <line key={i} x1="0" x2={W} y1={(H / 5) * i} y2={(H / 5) * i} style={{ stroke: 'var(--chart-grid)' }} />
      ))}
      {series.map((s, i) => {
        const path = smoothLine(s.values, W, H, max);
        return (
          <g key={i} className="dash-glow" style={{ ['--glow' as string]: s.color }}>
            <path d={path.area} fill={`url(#${uid}-${i})`} />
            <path d={path.line} fill="none" style={{ stroke: s.color }} strokeWidth="3" vectorEffect="non-scaling-stroke" />
          </g>
        );
      })}
    </svg>
  );
}

/** Halbkreis-Anzeige für eine Quote; Farbe nach Zielerreichung. */
export function ArcGauge({ value, label }: { value: number; label: string }) {
  const uid = useId().replace(/:/g, '');
  const cx = 115;
  const cy = 118;
  const r = 90;
  const clamped = Math.max(0, Math.min(100, value));
  const a = Math.PI + (clamped / 100) * Math.PI;
  const text = `${clamped.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
  return (
    <svg viewBox="0 0 230 150" role="img" aria-label={`${label}: ${text}`} className="dash-gauge">
      <defs>
        <linearGradient id={uid}>
          <stop offset="0" style={{ stopColor: 'var(--danger)' }} />
          <stop offset="0.5" style={{ stopColor: 'var(--warning)' }} />
          <stop offset="1" style={{ stopColor: 'var(--success)' }} />
        </linearGradient>
      </defs>
      <path d={`M${cx - r},${cy} A${r},${r} 0 0 1 ${cx + r},${cy}`} fill="none" style={{ stroke: 'var(--surface-3)' }} strokeWidth="16" strokeLinecap="round" />
      <g className="dash-glow" style={{ ['--glow' as string]: 'var(--warning)' }}>
        <path
          d={`M${cx - r},${cy} A${r},${r} 0 0 1 ${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`}
          fill="none"
          stroke={`url(#${uid})`}
          strokeWidth="16"
          strokeLinecap="round"
        />
      </g>
      <text x={cx} y={cy - 12} textAnchor="middle" fontSize="32" fontWeight="800" fill="var(--text)">
        {text}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="12" fill="var(--text-subtle)">
        {label}
      </text>
    </svg>
  );
}

/** Isometrische Halbringe: je Quote ein Ring, gefüllt von links nach rechts. */
export function HalfRings({ items }: { items: { label: string; percent: number; color: string }[] }) {
  const P = proj(180, 80);
  const n = items.length;
  const width = Math.min(20, 66 / n + 8);
  const gapStep = width + 5;
  const outer = (i: number) => 120 - i * gapStep;
  const order = items.map((_, i) => i).reverse();
  return (
    <svg viewBox="0 0 360 200" role="img" aria-label={`Quoten im Überblick: ${items.map((it) => `${it.label} ${Math.round(it.percent)} %`).join(', ')}`} className="dash-3d">
      {order.map((i) => {
        const it = items[i];
        const p = Math.max(0, Math.min(100, it.percent));
        return (
          <g key={i}>
            <Polys
              items={ringPolys(P, outer(i), outer(i) - width, 14, [{ v: 100 - p || 0.001, color: 'var(--surface-3)' }, { v: p || 0.001, color: it.color }], {
                start: 0,
                sweep: Math.PI,
                gap: 0.005,
              })}
            />
          </g>
        );
      })}
      {items.map((it, i) => (
        <Tag key={i} x={180 - (outer(i) - width / 2) * 1.2247} y={i % 2 ? 90 : 62} text={`${Math.round(it.percent)}%`} color={it.color} />
      ))}
    </svg>
  );
}

/** Isometrische Pfeile in einer waagerechten Reihe, Höhe nach Wert. */
export function ArrowRow({ items }: { items: { label: string; value: number; color: string }[] }) {
  const n = items.length;
  const P = proj(240 - (n - 1) * 34.64 - 5, 180);
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <svg viewBox="0 0 480 260" role="img" aria-label={`Erledigte Aufträge je Kalenderwoche: ${items.map((it) => `${it.label} ${it.value}`).join(', ')}`} className="dash-3d">
      {items.map((it, i) => {
        const h = (it.value / max) * 120 + 16;
        const x = i * 40;
        const y0 = -i * 40;
        const [tx, ty] = P(x + 12, y0 + 11, h + 26);
        const [bx, by] = P(x + 12, y0 + 11, 0);
        return (
          <g key={i}>
            <g className="dash-glow" style={{ ['--glow' as string]: it.color }}>
              <Polys items={arrowPolys(P, x, y0, 24, h, 22, it.color)} />
            </g>
            <line x1={tx} y1={ty + 12} x2={tx} y2={P(x + 12, y0 + 11, h)[1]} style={{ stroke: 'var(--text-subtle)' }} strokeDasharray="3 3" />
            <Tag x={tx} y={ty} text={String(it.value)} color={it.color} />
            <text x={bx} y={by + 40} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--text-subtle)">
              {it.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
